import { Vector2 } from '../../../odie';
import { GameScene } from '../../GameScene';
import { IEffect } from '../../actions/effects/IEffect';
import { LaserBoxBlockComponent } from './LaserBoxBlockComponent';
import { getCollisionTracker } from '../../util/CollisionTracker';
import gsap from 'gsap';
import { mapFromViewPosition } from '../../util/mapTools';
import { waitForItPromise } from 'src/lib/utils';

export type LaserBoxShootEffectOptions = {
  laserBox: LaserBoxBlockComponent;
};

const DESTROY_DELAY_DURATION = 1.05;
const DESTROY_DURATION = 0.12;
const DEACTIVATION_DELAY_DURATION = 1.25;

// Reusable Vector2 instance to avoid allocations during execute
const tempPosInput = new Vector2();

export class LaserBoxBlockShootEffect implements IEffect {
  private readonly scene: GameScene;
  private options: LaserBoxShootEffectOptions;

  constructor(scene: GameScene, options: LaserBoxShootEffectOptions) {
    this.scene = scene;
    this.options = options;
  }

  public async execute() {
    const { laserBox } = this.options;

    // Play the laser activation animation
    void laserBox.view.playActivateAnimation();

    // Wait for a brief moment before applying the attack to match the laser animation
    await waitForItPromise(DESTROY_DELAY_DURATION * 1000);

    // Perform horizontal/vertical attack from the laser box based on orientation
    const orientation = laserBox.orientation;
    const { x, y } = laserBox.entity.view;
    const pos = mapFromViewPosition(tempPosInput.set(x, y));

    // Extract primitive values to avoid repeated property access
    const posX = pos.x;
    const posY = pos.y;

    // Get map and collision tracker
    const map = this.scene.sessionEntity.c.map;
    const collisionTracker = getCollisionTracker();

    // Calculate target counts in each direction (no Vector2 arrays needed)
    let leftCount: number;
    let rightCount: number;

    if (orientation === 'horizontal') {
      leftCount = posX; // targets from 0 to posX-1
      rightCount = map.columns - posX - 1; // targets from posX+1 to columns-1
    } else {
      leftCount = posY; // targets from 0 to posY-1
      rightCount = map.rows - posY - 1; // targets from posY+1 to rows-1
    }

    // Calculate timing for staggered destruction
    const maxDistance = Math.max(leftCount, rightCount);
    const delayPerStep = maxDistance > 0 ? DESTROY_DURATION / maxDistance : 0;

    // Create GSAP timeline for staggered attacks
    const timeline = gsap.timeline();

    // Add attacks to timeline with staggered timing
    for (let step = 0; step < maxDistance; step++) {
      const delay = step * delayPerStep;

      // Attack at current distance on both sides simultaneously
      if (step < leftCount) {
        // Calculate target coordinates (captured by closure as primitives)
        const targetX = orientation === 'horizontal' ? posX - 1 - step : posX;
        const targetY = orientation === 'horizontal' ? posY : posY - 1 - step;
        timeline.call(
          () => {
            collisionTracker.collideAt(
              map,
              { x: targetX, y: targetY },
              'attack',
              {
                type: 'laserBox',
              },
            );
          },
          [],
          delay,
        );
      }

      if (step < rightCount) {
        // Calculate target coordinates (captured by closure as primitives)
        const targetX = orientation === 'horizontal' ? posX + 1 + step : posX;
        const targetY = orientation === 'horizontal' ? posY : posY + 1 + step;
        timeline.call(
          () => {
            collisionTracker.collideAt(
              map,
              { x: targetX, y: targetY },
              'attack',
              {
                type: 'laserBox',
              },
            );
          },
          [],
          delay,
        );
      }
    }

    // Wait for timeline to complete
    await timeline.then();

    // Wait for a brief moment to let the laser effect linger
    await waitForItPromise(DEACTIVATION_DELAY_DURATION * 1000);

    // Play the laser deactivation animation
    await laserBox.view.playDeactivateAnimation();
  }
}
